import {
  useWSModal,
  useWSSnackbar,
  WSActions,
  WSAlert,
  WSElement,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  EngagementStatus,
  IEngagement
} from "@wingspanhq/payments/dist/interfaces";
import { WSErrorMessage } from "../../../components/WSErrorMessage/WSErrorMessage";
import React from "react";
import { ErrorContextKey } from "../../../services/platform";
import { ActionEngagementDetails } from "../components/ActionEngagementDetails";
import { useMutationUpdateEngagement } from "../../../query/engagements/mutations/useMutationUpdateEngagement";

export interface ArchiveEngagementTypeModalProps {
  engagement: IEngagement;
  onClose: () => void;
}

export const ArchiveEngagementTypeModal: React.FC<
  ArchiveEngagementTypeModalProps
> = ({ engagement, onClose }) => {
  const { openSnackbar } = useWSSnackbar();
  const [updateEngagementType, updateEngagementTypeMeta] =
    useMutationUpdateEngagement();

  const handleArchive = async () => {
    await updateEngagementType({
      engagementId: engagement.engagementId,
      status: EngagementStatus.Inactive
    });

    openSnackbar({
      theme: "dark",
      type: "success",
      message: "Engagement archived"
    });
    onClose();
  };

  return (
    <>
      <WSText.ParagraphSm mb="L" weight="book">
        Are you sure you want to archive this engagement?
      </WSText.ParagraphSm>
      <ActionEngagementDetails engagement={engagement} />

      <WSAlert
        my="2XL"
        theme="info"
        icon="info-circle"
        size="M"
        title="Archiving engagement - what to expect:"
      >
        <WSElement as="ul" ml="L">
          <li>
            Any archived assignments associated with this engagement will remain
            archived and cannot be restored while the engagement remains
            archived.
          </li>
          <li>No changes can be made to this engagement.</li>
          <li>No additional invoices can be created for this engagement.</li>
          <li>
            Incomplete contractor requirements exclusively associated with this
            engagement will also be archived.
          </li>
        </WSElement>
        Existing invoices will remain on the Invoicing page until you delete
        them. You can always restore the engagement when needed.
      </WSAlert>

      <WSErrorMessage
        error={updateEngagementTypeMeta.error}
        mb="XL"
        contextKey={ErrorContextKey.Engagement}
      />

      <WSActions
        alignment="fill"
        buttons={[
          {
            label: "Cancel",
            onClick: onClose,
            kind: "Secondary"
          },
          {
            label: "Archive engagement",
            loading: updateEngagementTypeMeta.isLoading,
            onAsyncClick: handleArchive,
            kind: "Primary"
          }
        ]}
      />
    </>
  );
};

export const useArchiveEngagementTypeModal = () => {
  return useWSModal(ArchiveEngagementTypeModal, {
    title: "Archive engagement",
    size: "M"
  });
};
